/**
 * English (en-US) translation.
 * Must implement EXACTLY the same shape as `pt.ts` (enforced by `Translations` type).
 */
import type { Translations } from '..';

const en: Translations = {
  meta: {
    htmlLang: 'en-US',
  },
  common: {
    loading: 'Loading...',
    close: 'Close',
    cancel: 'Cancel',
    ok: 'OK',
    continue: 'Continue',
    back: 'Back',
    error: 'Error',
    attention: 'Attention',
    success: 'Success',
    oops: 'Oops...',
    copied: 'Copied!',
  },
  layout: {
    loading: 'Loading...',
    errorTitle: 'Failed to load settings',
    errorBody: 'We could not load the settings for this page. Please try again in a moment.',
  },
  languageSwitcher: {
    label: 'Language',
    pt: 'Português',
    en: 'English',
    es: 'Español',
  },
  /** Home — step 1: enter the phone number. */
  home: {
    title: 'Recarga Fácil',
    subtitle: 'Enter the number you want to top up',
    phoneLabel: 'Mobile Number',
    phonePlaceholder: '(00) 00000-0000',
    submit: 'Confirm Number',
    errors: {
      phoneRequired: 'Please enter your phone number',
      phoneLength: 'The number must include the area code + 9 digits',
      lineNotFound: 'Number not found.',
      lineCheckFailed: 'Error validating the number. Please try again.',
    },
  },
  /** Home — step 2: SMS code validation. */
  otp: {
    title: 'Security',
    /** {{phone}} is replaced by the formatted number. */
    subtitle: 'We sent an SMS code to {{phone}}',
    codeLabel: 'SMS Code',
    codePlaceholder: '000000',
    submit: 'Validate Access',
    /** {{seconds}} is the time left on the resend cooldown. */
    resendCountdown: 'Resend code ({{seconds}}s)',
    resend: 'Resend code',
    resent: 'Code resent successfully.',
    errors: {
      length: 'The code must have 6 digits',
      invalid: 'Invalid or expired code.',
      validateFailed: 'Error validating code',
      resendFailed: 'Failed to resend the SMS.',
      sessionExpired: 'Session expired. Please start over.',
      cooldown: 'Please wait a moment before requesting a new code for this number.',
    },
  },
  /** Plan list and payment method selection. */
  plans: {
    pageTitle: 'Choose Your Plan',
    heading: 'Choose the right plan',
    changeNumber: 'Change number',
    customerFallback: 'Customer',
    planLabel: 'Plan',
    networkBadge: '4G/5G',
    currency: 'R$',
    perMonth: '/month',
    select: 'Select Plan',
  },
  /** "How would you like to pay?" modal. */
  payment: {
    chooseTitle: 'How would you like to pay?',
    pix: {
      name: 'PIX',
      hint: 'Instant approval',
      badge: 'Save time',
    },
    card: {
      name: 'Credit Card',
      hint: 'Pay now, right here on the site',
    },
    boleto: {
      name: 'Boleto (bank slip)',
      hint: 'May take up to 3 days',
    },
    processingTitle: 'Processing...',
    processingBody: 'Please wait while we generate your payment',
    errors: {
      generic: 'Error processing your request.',
      timeout: 'The request took too long. Please check your connection.',
      noPix: 'PIX details were not returned. Please try again.',
      noBoleto: 'Boleto details were not returned by the API.',
    },
  },
  /** PIX modal + confirmation polling. */
  pix: {
    title: 'PIX Payment',
    instructions: 'Scan the QR Code or use the copy and paste code.',
    copyCode: 'Copy Pix Code',
    qrAlt: 'PIX QR Code',
    confirmedTitle: 'Payment Confirmed!',
    confirmedBody: 'Your top-up was completed successfully.',
    timeoutTitle: 'Time expired',
    timeoutBody:
      'We have not received your payment confirmation. If you have already paid with PIX, please wait a few minutes. Otherwise, generate a new code.',
    waiting: 'Waiting for payment confirmation...',
  },
  /** Boleto modal. */
  boleto: {
    title: 'Boleto (bank slip)',
    instructions: 'Use the barcode or open the link.',
    copyBarcode: 'Copy Barcode',
    open: 'Open Boleto (PDF)',
  },
  /** Credit card payment screen. */
  creditCard: {
    pageTitle: 'Credit Card',
    summaryTitle: 'Order Summary',
    summarySubtitle: 'Check the details of your top-up.',
    selectedPlan: 'Selected Plan',
    amount: 'Amount',
    line: 'Number',
    document: 'Document',
    formTitle: 'Payment Details',
    nameLabel: 'Name on Card',
    namePlaceholder: 'Exactly as printed on the card',
    numberLabel: 'Card Number',
    numberPlaceholder: '0000 0000 0000 0000',
    expiryLabel: 'Expiration',
    expiryPlaceholder: 'MM/AAAA',
    cvvLabel: 'CVV',
    cvvPlaceholder: '123',
    /** Accessible label for the brand icon shown inside the card number field. */
    brandIconAlt: 'Card brand',
    recurrenceLabel: 'Enable recurring charges on this card',
    recurrenceHint: 'The payment will be renewed automatically every month.',
    submit: 'Complete Payment',
    submitting: 'Processing...',
    secureFooter: '100% Secure Environment. Your data is protected.',
    approvedTitle: 'Payment Approved!',
    approvedBody: 'Your top-up was completed successfully.',
    backHome: 'Back to home',
    errors: {
      generic: 'Error processing payment.',
      failed: 'Processing failed',
      invalidName: 'Enter the name exactly as printed on the card.',
      invalidNumber: 'Invalid card number.',
      invalidExpiry: 'Invalid expiration date. Use the MM/AAAA format.',
      expiredCard: 'Card expired.',
      invalidCvv: 'Invalid CVV.',
      recaptchaFailed: 'We could not complete the security check. Reload the page and try again.',
      recaptchaUnavailable: 'Card payment is temporarily unavailable. Please use PIX or boleto.',
    },
  },
  /**
   * Flow A (legacy) — the `/renova_plano?t={tipo}&id={id}` page, consumed by the
   * Link de Venda Direta product. The wording mirrors the Blade view.
   */
  legacyPayment: {
    /** {{name}} is the customer first name. */
    greeting: 'Hello, {{name}}',
    /** {{product}} comes from `product.*` below. */
    subtitle: 'Complete the payment for your {{product}}',
    product: {
      recarga: 'top-up',
      ativacao: 'activation',
      compra: 'purchase',
    },
    pixDelay: 'The payment may take up to 15 minutes to clear.',
    pixCopyIntroHighlight: 'Copy and paste',
    pixCopyIntro: 'the code below into your banking app, under the PIX option.',
    pixCopyButton: 'Copy code',
    pixScanHighlight: 'Scan the QRCode',
    pixScanIntro: 'below after opening Pix in your banking app',
    pixQrAlt: 'Your QR code',
    boletoDelay: 'The payment may take up to 2 business days to clear.',
    boletoView: 'VIEW BOLETO',
    boletoCopy: 'COPY CODE',
    linkIntro: 'Choose whether to pay with pix, credit card or boleto.',
    linkButton: 'CHOOSE PAYMENT METHOD',
    supportPrefix: 'Questions? Talk to our',
    supportLink: 'support team',
    rightsReserved: 'All rights reserved',
    notFound: 'Payment not found.',
  },
  /** Shared footer. */
  footer: {
    customerService: 'Customer Service',
    whatsapp: 'WhatsApp',
    androidApp: 'Download the App on Android',
    googlePlay: 'Google Play',
    iosApp: 'Download the App on iPhone',
    appStore: 'App Store',
    website: 'Visit our website',
    /** {{year}} is the current year. */
    copyright:
      '© {{year}} Nogle Tech. All rights reserved. Reproduction without authorization is prohibited. This is a secure environment. All data is encrypted to protect your privacy.',
    nogleAlt: 'Marca Nogle',
  },
  /** Inactive brand / unknown route page (equivalent to service_unavailable). */
  notFound: {
    pageTitle: 'Page not found',
    code: '404',
    title: 'Page not found',
    body: 'Sorry, we could not find the page you are looking for.',
    /** {{year}} is the current year. */
    copyright: '© {{year}} Recarga Fácil. All rights reserved.',
  },
  /** API layer error messages. */
  api: {
    sessionExpired: 'Your session has expired. Please enter your number again.',
  },
};

export default en;
